import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { first, map, mergeMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { OAuthModuleConfig, OAuthResourceServerConfig } from './oauth-module.config';

@Injectable()
export class ClientIdHttpInterceptor implements HttpInterceptor {
  serverConfig: OAuthResourceServerConfig;

  constructor(
    private readonly authService: AuthService,
    @Optional() moduleConfig?: OAuthModuleConfig
  ) {
    this.serverConfig = { sendAccessToken: false, ...moduleConfig?.resourceServer };
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.sendClientIdFor(req)) {
      return next.handle(req);
    }

    const clientIds$ = forkJoin({
      impersonatedClientId: this.authService.impersonatedClientId$.pipe(first()),
      signedInClientId: this.authService.signedInClientId$.pipe(first())
    });

    return clientIds$.pipe(
      map(({ impersonatedClientId, signedInClientId }) =>
        this.addClientIdRequestHeaders(req, impersonatedClientId, signedInClientId)
      ),
      mergeMap(r => next.handle(r))
    );
  }

  private addClientIdRequestHeaders(
    req: HttpRequest<unknown>,
    impersonatedClientId?: string,
    signedInClientId?: string
  ) {
    if (!impersonatedClientId && !signedInClientId) {
      return req;
    }

    let headers = req.headers;
    if (impersonatedClientId) {
      headers = req.headers.append(
        this.serverConfig.impersonatedClientIdHeader ?? 'X-Impersonated-MriClientId',
        impersonatedClientId
      );
    }
    if (signedInClientId) {
      headers = headers.append(this.serverConfig.signedInClientIdHeader ?? 'X-Current-MriClientId', signedInClientId);
    }
    return req.clone({ headers });
  }

  private sendClientIdFor(req: HttpRequest<unknown>): boolean {
    const url = req.url.toLowerCase();

    if (!this.serverConfig.sendClientId) {
      return false;
    }

    if (this.serverConfig.customUrlValidation) {
      return this.serverConfig.customUrlValidation(url);
    }

    if (this.serverConfig.allowedUrls) {
      return this.serverConfig.allowedUrls.some(u => url.startsWith(u));
    }

    return true;
  }
}
